import { useState } from "react";
import {
  dbk_searchUsServices,
  dbk_usServiceCategories,
  dbk_usServicesCopy as copy,
  dbk_usWorkflowCaveat
} from "../content/markets/usServices";
import { dbkUsFooterContent } from "../content/markets/usFooter";
import { SiteFooter } from "./SiteFooter";
import { dbk_UsSiteHeader as UsSiteHeader } from "./UnitedStatesLandingPage";
import "../styles/us-services.css";

// Prefix only local homepage anchors; legal, login and estimate URLs stay intact.
const catalogFooter = {
  ...dbkUsFooterContent,
  groups: dbkUsFooterContent.groups.map(group => ({
    ...group,
    links: group.links.map(link => ({
      ...link,
      href: link.href.startsWith("#") && !link.action ? `/${link.href}` : link.href
    }))
  }))
};

export function dbk_UnitedStatesServicesCatalog() {
  const [query, setQuery] = useState("");
  const [openCategories, setOpenCategories] = useState<ReadonlySet<string>>(
    () => new Set([dbk_usServiceCategories[0].id])
  );
  const searching = Boolean(query.trim());
  const workflows = dbk_searchUsServices(query);
  const groups = dbk_usServiceCategories.map(category => ({
    ...category,
    workflows: workflows.filter(workflow => workflow.category === category.title)
  })).filter(category => category.workflows.length);

  return <div className="db-page market-landing us-homepage us-services-page">
    <a className="skip-link" href="#main-content">Skip to content</a>
    <UsSiteHeader homepage={false} />
    <main id="main-content" className="db-main db-container us-catalog" aria-labelledby="catalog-title" tabIndex={-1}>
      <div className="us-catalog__intro">
        <p className="section-eyebrow">ACCOUNTING &amp; AI AGENT SERVICES</p>
        <h1 id="catalog-title">{copy.heading}</h1>
        {copy.intro.map(paragraph => <p className="us-catalog__intro-copy" key={paragraph}>{paragraph}</p>)}
        <a className="db-button db-button--primary" href={copy.estimateHref}>Request an Estimate</a>
      </div>
      <aside className="us-catalog__availability" aria-labelledby="catalog-availability">
        <h2 id="catalog-availability">Availability requires confirmation</h2>
        <p>{copy.availability}</p>
      </aside>
      <div className="us-catalog__search" role="search" aria-label="Services catalog">
        <label htmlFor="service-search">Search services and workflows</label>
        <input id="service-search" type="search" value={query} maxLength={160}
          onChange={event => setQuery(event.target.value)}
          placeholder="Try reconciliation, reports or agent review" aria-controls="service-results" />
        <p role="status" aria-live="polite" aria-atomic="true">
          {workflows.length} {workflows.length === 1 ? "workflow" : "workflows"} found
        </p>
        {query && <button className="db-button db-button--secondary" type="button"
          onClick={() => { setQuery(""); document.getElementById("service-search")?.focus(); }}>Clear search</button>}
      </div>
      <div id="service-results" className="us-catalog__results">
        {!workflows.length && <p>No workflows match your search. Try another term or clear the search.</p>}
        {groups.map(category => <details className="us-catalog__category" id={category.id}
          key={`${category.id}-${searching ? "search" : "browse"}`}
          open={searching || openCategories.has(category.id)}
          onToggle={event => {
            // Search expands matching groups without changing the saved browse state.
            if (searching) return;
            const isOpen = event.currentTarget.open;
            setOpenCategories(previous => {
              if (previous.has(category.id) === isOpen) return previous;
              const next = new Set(previous);
              if (isOpen) next.add(category.id);
              else next.delete(category.id);
              return next;
            });
          }}>
          <summary><h2>{category.title} <span>({category.workflows.length})</span></h2></summary>
          <div className="us-catalog__cards">
            {category.workflows.map(workflow => <article className="us-catalog__card"
              id={`service-${workflow.id}`} key={workflow.id} data-source-evidence={workflow.evidenceId}
              aria-labelledby={`service-${workflow.id}-title`}>
              <h3 id={`service-${workflow.id}-title`}>{workflow.title}</h3>
              <p className="us-catalog__caveat">{dbk_usWorkflowCaveat(workflow.id)}</p>
              <p>{workflow.body}</p>
              <dl>
                <dt>How it helps</dt><dd>{workflow.benefit}</dd>
                <dt>Your control</dt><dd>{workflow.review}</dd>
                <dt>Where you use it</dt><dd>{workflow.access}</dd>
              </dl>
            </article>)}
          </div>
        </details>)}
      </div>
      <aside className="us-catalog__boundary" aria-labelledby="catalog-boundaries">
        <h2 id="catalog-boundaries">{copy.importantHeading}</h2>
        <p>{copy.important}</p>
        <p>{copy.regionalLimit}</p>
        <p>{copy.integrationLimit} Check the <a href={copy.quickBooksHref}>QuickBooks integration page</a> for current readiness and supported operations.</p>
      </aside>
      <a className="db-button db-button--primary us-catalog__bottom-estimate" href={copy.estimateHref}>Request an Estimate</a>
    </main>
    <SiteFooter content={catalogFooter} logoSrc={copy.logoSrc} />
  </div>;
}
